
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Phone, CheckCircle, Clock, RefreshCw, User, Eye, AlertCircle } from "lucide-react";
import PartnerDashboardHeader from "@/components/partners/PartnerDashboardHeader";
import { LeadStageType, ServiceType } from "@/context/LeadContext";
import { format } from "date-fns";
import { el } from "date-fns/locale";
import LeadStatusReminderBadge from "@/components/ui-custom/LeadStatusReminderBadge";

interface Partner {
  id: string;
  name: string;
  phone: string;
  email: string;
  location: string;
}

interface Lead {
  id: string;
  name: string;
  phone: string;
  address: string;
  postalCode: string;
  service: ServiceType;
  timeframe: string;
  stage: LeadStageType;
  createdAt: string;
  viewed: boolean;
}

const PartnerDashboard = () => {
  const { partnerId } = useParams<{ partnerId: string }>();
  const [partner, setPartner] = useState<Partner | null>(null);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    pending: 0,
    new: 0,
    viewed: 0,
    successRate: 0
  });
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("recent");

  useEffect(() => {
    const checkAuthorization = () => {
      const adminId = localStorage.getItem("adminId");
      const loggedInPartnerId = sessionStorage.getItem("partnerId");
      
      if (!adminId && (!loggedInPartnerId || loggedInPartnerId !== partnerId)) {
        navigate("/partner/login");
        return false;
      }
      
      return true;
    };
    
    const fetchPartnerData = async () => {
      if (!checkAuthorization()) return;
      
      try {
        setIsLoading(true);
        
        const { data: partnerData, error: partnerError } = await supabase
          .from('partners')
          .select('id, name, phone, email, location')
          .eq('id', partnerId)
          .single();
        
        if (partnerError) throw partnerError;
        setPartner(partnerData);
        
        const { data: leadsData, error: leadsError } = await supabase
          .from('leads')
          .select('*')
          .eq('partner_id', partnerId)
          .order('created_at', { ascending: false });
        
        if (leadsError) throw leadsError;
        
        const formattedLeads = leadsData.map((lead) => ({
          id: lead.id,
          name: lead.name,
          phone: lead.phone,
          address: lead.address,
          postalCode: lead.postal_code || "",
          service: lead.service as ServiceType,
          timeframe: lead.timeframe,
          stage: (lead.stage || 'Νέο') as LeadStageType,
          createdAt: lead.created_at,
          viewed: lead.link_opened || false
        }));
        
        setLeads(formattedLeads);
        
        const total = formattedLeads.length;
        const completed = formattedLeads.filter(lead => lead.stage === 'έκλεισεΗΔουλειά').length;
        const pending = formattedLeads.filter(lead => lead.stage === 'σεΕκκρεμότητα').length;
        const newLeads = formattedLeads.filter(lead => lead.stage === 'Νέο').length;
        const viewed = formattedLeads.filter(lead => lead.viewed).length;
        
        setStats({
          total,
          completed,
          pending,
          new: newLeads,
          viewed,
          successRate: total > 0 ? Math.round((completed / total) * 100) : 0
        });
        
      } catch (error) {
        console.error('Error fetching partner data:', error);
        toast({
          title: "Σφάλμα",
          description: "Δεν ήταν δυνατή η φόρτωση των δεδομένων.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPartnerData();
  }, [partnerId, navigate, toast]);

  const refreshData = () => {
    setIsLoading(true);
    setTimeout(() => {
      window.location.reload();
    }, 300);
  };

  const getServiceLabel = (service: string) => {
    switch (service) {
      case "εξοικονομώ": return "Εξοικονομώ";
      case "αντλίεςΘερμότητας": return "Αντλίες Θερμότητας";
      case "φωτοβολταϊκά": return "Φωτοβολταϊκά";
      case "κλιματιστικά": return "Κλιματιστικά";
      case "άλλο": return "Άλλο";
      default: return service;
    }
  };

  const getStageLabel = (stage: string) => {
    switch (stage) {
      case "σεΕκκρεμότητα": return "Σε Εκκρεμότητα";
      case "έκλεισεΗΔουλειά": return "Έκλεισε η Δουλειά";
      case "ήθελεΠληροφορίες": return "Ήθελε Πληροφορίες";
      case "Νέο": return "Νέο";
      default: return stage;
    }
  };

  const getStageColor = (stage: string) => {
    switch (stage) {
      case "σεΕκκρεμότητα": return "bg-yellow-100 text-yellow-800";
      case "έκλεισεΗΔουλειά": return "bg-green-100 text-green-800";
      case "ήθελεΠληροφορίες": return "bg-blue-100 text-blue-800";
      case "Νέο": return "bg-purple-100 text-purple-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Φόρτωση στοιχείων...</p>
        </div>
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <h1 className="text-2xl font-bold mb-4">Ο συνεργάτης δεν βρέθηκε</h1>
        <Button onClick={() => navigate("/partner/login")}>
          Επιστροφή στη σύνδεση
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PartnerDashboardHeader 
        partnerName={partner.name} 
        partnerId={partner.id}
        partnerPhone={partner.phone}
        partnerEmail={partner.email}
      />
      
      <div className="container mx-auto px-4 pb-8">
        <div className="bg-blue-50 border border-blue-200 rounded-lg mb-6 p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-6 w-6 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-800 mb-1">Σημαντική υπενθύμιση</h3>
              <p className="text-blue-700 text-sm">Η άμεση επικοινωνία με τους υποψήφιους πελάτες αυξάνει σημαντικά τις πιθανότητες συνεργασίας. Προτείνουμε να επικοινωνείτε εντός 1 ώρας από την ανάθεση του Lead για βέλτιστα αποτελέσματα.</p>
            </div>
          </div>
        </div>
        
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Πίνακας Ελέγχου</h2>
          <Button size="sm" variant="outline" onClick={refreshData} className="flex items-center gap-1">
            <RefreshCw className="h-4 w-4 mr-1" />
            Ανανέωση
          </Button>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 flex flex-col items-center justify-center text-center">
              <User className="h-8 w-8 text-blue-500 mb-2" />
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-xs text-muted-foreground">Συνολικά Leads</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex flex-col items-center justify-center text-center">
              <CheckCircle className="h-8 w-8 text-green-500 mb-2" />
              <p className="text-2xl font-bold">{stats.completed}</p>
              <p className="text-xs text-muted-foreground">Ολοκληρωμένα</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex flex-col items-center justify-center text-center">
              <Clock className="h-8 w-8 text-yellow-500 mb-2" />
              <p className="text-2xl font-bold">{stats.pending}</p>
              <p className="text-xs text-muted-foreground">Σε Εκκρεμότητα</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex flex-col items-center justify-center text-center">
              <Eye className="h-8 w-8 text-purple-500 mb-2" />
              <p className="text-2xl font-bold">{stats.successRate}%</p>
              <p className="text-xs text-muted-foreground">Ποσοστό Επιτυχίας</p>
            </CardContent>
          </Card>
        </div>
        
        <Tabs defaultValue="recent" value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="mb-4">
            <TabsTrigger value="recent">Πρόσφατα Leads</TabsTrigger>
            <TabsTrigger value="all">Όλα τα Leads</TabsTrigger>
          </TabsList>
          
          <TabsContent value="recent">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Πρόσφατες αναθέσεις</CardTitle>
              </CardHeader>
              <CardContent>
                {leads.length > 0 ? (
                  <div className="space-y-4">
                    {leads.slice(0, 5).map((lead) => (
                      <div key={lead.id} className="border rounded-lg p-4 bg-white">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium">{lead.name}</h3>
                              <LeadStatusReminderBadge 
                                lastUpdated={lead.createdAt}
                                stage={lead.stage}
                              />
                            </div>
                            <div className="flex items-center mt-1 text-sm text-gray-500">
                              <Phone className="h-3 w-3 mr-1" />
                              <span>{lead.phone}</span>
                            </div>
                            <p className="text-sm mt-2 text-gray-600">{lead.address}</p>
                          </div>
                          <div className="flex flex-col items-end">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStageColor(lead.stage)}`}>
                              {getStageLabel(lead.stage)}
                            </span>
                            <span className="text-xs text-gray-500 mt-1">
                              {format(new Date(lead.createdAt), 'dd/MM/yyyy', { locale: el })}
                            </span>
                          </div>
                        </div>
                        <div className="mt-3 flex justify-between items-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {getServiceLabel(lead.service)}
                          </span>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => navigate(`/lead/${lead.id}`)}
                          >
                            Προβολή
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">Δεν υπάρχουν αναθέσεις ακόμα</p>
                  </div>
                )}
                
                {leads.length > 5 && (
                  <div className="mt-4 text-center">
                    <Button 
                      variant="outline" 
                      onClick={() => setActiveTab("all")}
                    >
                      Προβολή Όλων ({leads.length})
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="all">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Όλες οι αναθέσεις</CardTitle>
              </CardHeader>
              <CardContent>
                {leads.length > 0 ? (
                  <div className="space-y-4">
                    {leads.map((lead) => (
                      <div key={lead.id} className="border rounded-lg p-4 bg-white">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium">{lead.name}</h3>
                              <LeadStatusReminderBadge 
                                lastUpdated={lead.createdAt}
                                stage={lead.stage}
                              />
                            </div>
                            <div className="flex items-center mt-1 text-sm text-gray-500">
                              <Phone className="h-3 w-3 mr-1" />
                              <span>{lead.phone}</span>
                            </div>
                            <p className="text-sm mt-2 text-gray-600">{lead.address}</p>
                          </div>
                          <div className="flex flex-col items-end">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStageColor(lead.stage)}`}>
                              {getStageLabel(lead.stage)}
                            </span>
                            <span className="text-xs text-gray-500 mt-1">
                              {format(new Date(lead.createdAt), 'dd/MM/yyyy', { locale: el })}
                            </span>
                          </div>
                        </div>
                        <div className="mt-3 flex justify-between items-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {getServiceLabel(lead.service)}
                          </span>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => navigate(`/lead/${lead.id}`)}
                          >
                            Προβολή
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">Δεν υπάρχουν αναθέσεις ακόμα</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default PartnerDashboard;
